/**
 * "Confirm in your wallet" dialog shared by the order ticket (#677) and cancels (#678).
 *
 * MetaMask only shows an opaque hash (connectionId) for L1 actions, so this dialog shows the human summary
 * (passed as children) while the wallet prompt is open. It cannot be dismissed: it closes when signing ends.
 */
import type { ReactNode } from 'react';
import Modal from '../components/Modal';
import { chainPromptText, walletProfile } from '../wallet/compat';
import { useWallet } from '../wallet/context';
import { useT } from '../i18n/useT';

export function WalletConfirmModal({
  open,
  what,
  children,
}: {
  open: boolean;
  /** 'order' keeps the order-ticket wording; 'cancel' explains a cancel signature. */
  what: 'order' | 'cancel';
  children: ReactNode;
}) {
  const { walletInfo, provider } = useWallet();
  const t = useT();
  const prompt = chainPromptText(walletProfile(walletInfo?.rdns ?? null, provider));
  return (
    <Modal open={open} onClose={() => undefined} title={t('ticket.wallet_confirm.title')}>
      {open && (
        <div className="space-y-3" data-testid="wallet-confirm">
          {children}
          {what === 'order' ? (
            <p className="text-xs text-muted">{t('ticket.wallet_confirm.order', { prompt })}</p>
          ) : (
            <p className="text-xs text-muted">{t('ticket.wallet_confirm.cancel', { prompt })}</p>
          )}
        </div>
      )}
    </Modal>
  );
}
